import { Box, Collapse, GridLegacy as Grid, IconButton, Stack, SxProps, Typography } from '@mui/material'
import { FeatureFlagIdUserOrigin } from 'c1gen/features'
import { C1Button } from 'c1ui/components/C1Button/C1Button'
import { C1Chip } from 'c1ui/components/C1Chip'
import { C1Dialog, C1DialogActions, C1DialogContent, C1DialogTitle } from 'c1ui/components/C1Dialog/C1Dialog'
import { C1Tooltip } from 'c1ui/components/C1Tooltip/C1Tooltip'
import { C1AppUsersList, AppUserListColumns } from 'components/core/app-user/C1AppUsersList'
import { C1AutocompleteMultipleAsyncHook, C1AutocompleteSingleAsyncHook, renderTagsWithLocked, SelectionsModalProps, SingleSelectionModalProps } from 'components/core/autocomplete'
import { APIError } from 'components/core/errors/api'
import { RenderedSelectOneProps, renderLabel, SelectOne, SelectOneHookProps } from 'components/core/search'
import { C1UsersList, UserListColumns } from 'components/core/user/C1UsersList'
import { useFeatureChecker } from 'components/no-ui/capability'
import { sxc } from 'components/no-ui/styles/styleTypes'
import { AgentInstructionsTypeToText } from 'components/pages/admin/settings/copilot/AgentInstructions'
import { AppUserDisplayName } from 'components/pages/app/users/app-user-display-name'
import { AppUserFilterKeys } from 'components/pages/app/users/search'
import { UserFilterKeys } from 'components/pages/user/search'
import { UserDisplayName } from 'components/pages/user/user-display-name'
import { AppUserValue } from 'lib/actions/apps/app-users'
import { UserValue } from 'lib/actions/user'
import { EditReturn } from 'lib/rpc'
import { AppUser, AppUserRef, AppUserServiceSearchRequest } from 'pbts/c1/api/app/v1/app_user'
import { AgentInstructionsType, AgentRef, AgentView, SearchAgentsRequest, SearchManagedUsersForAccessRequestsRequest, SearchUsersRequest, User, UserOrigin, UserRef, UserType } from 'pbts/c1/api/user/v1/user'
import React, { ComponentProps, FC, ReactNode, useEffect, useState, useCallback, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { searchAgentsApi } from 'redux_toolkit/agents'
import { searchAppUsersApi } from 'redux_toolkit/app-users'
import { AppUserViewExpanded } from 'redux_toolkit/apps'
import { managedUserSearchForAccessRequestsApi, userSearchApi } from 'redux_toolkit/users'
import { UserStyles } from './user-styles'

export type saveFunc = (users: User[]) => void

export type ClickAwaySearchUsersProps = {
    users: User[]
    onSave: saveFunc
    saveState: EditReturn
    canUpdate: boolean
    emptySelectState?: ReactNode
    rowRef?: React.RefObject<HTMLDivElement | null>
}

export type DisplayUserNamesProps = {
    allowEditClick?: boolean
    proposedOptions: Array<User | undefined>
    emptySelectState?: ReactNode
    readOnly?: boolean
    expandable?: boolean
    margin?: boolean
    maxWidth?: string,
    /** User IDs that are immutable/managed (will show an indicator) */
    immutableUserIds?: string[]
}

export const DisplayUserNamesWithFetch: React.FC<Omit<DisplayUserNamesProps, 'proposedOptions'> & { userIds: string[] }> = ({
    userIds, ...rest
}) => {
    if (userIds.length === 0) return null

    const refs = userIds.map((id) => UserRef.create({ id }))

    const { data } = userSearchApi.useSearch(SearchUsersRequest.create({ refs }))

    return <DisplayUserNames proposedOptions={data.flat()} {...rest} />
}

export const DisplayUserNames: React.FC<DisplayUserNamesProps> = ({
    allowEditClick,
    proposedOptions,
    emptySelectState,
    readOnly = false,
    expandable = false,
    margin,
    maxWidth,
    immutableUserIds = [],
}) => {
    const intl = useIntl()
    let emptyState = <></>
    if (proposedOptions.length === 0) {
        emptyState = (
            <Grid item sx={UserStyles.empty}>
                {emptySelectState}
            </Grid>
        )
    }

    const [expanded, setExpanded] = React.useState(false)

    const UserList = (): React.ReactElement => {
        return (
            <>
                {proposedOptions
                    .slice() // Make a copy. proposedOptions is read-only.
                    .sort((a, b) => {
                        // Sort alphabetically
                        const textA = a ? a.displayName.toUpperCase() : ''
                        const textB = b ? b.displayName.toUpperCase() : ''
                        return textA < textB ? -1 : textA > textB ? 1 : 0
                    })
                    .map((user, index) => {
                        if (user) {
                            const isImmutable = immutableUserIds.includes(user.id)
                            return (
                                <Grid
                                    container
                                    sx={sxc([allowEditClick, UserStyles.hand])}
                                    direction="row"
                                    spacing={0}
                                    key={`user_${user.id}`}
                                >
                                    <Grid item>
                                        <Box sx={sxc([index !== 0, { marginTop: 2 }])}>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <UserDisplayName
                                                    displayNameVariant="body1"
                                                    user={user}
                                                    readOnly={readOnly}
                                                    includeDetails={false}
                                                    showTooltip
                                                    maxWidth={maxWidth}
                                                    margin={margin}
                                                />
                                                {isImmutable && (
                                                    <C1Tooltip title={intl.formatMessage({
                                                        id: 'search_users_immutable_tooltip',
                                                        defaultMessage: 'This owner is managed by the system and cannot be removed',
                                                    })}
                                                    >
                                                        <Box component="span">
                                                            <C1Chip
                                                                color="info"
                                                                size="small"
                                                                label={intl.formatMessage({
                                                                    id: 'display_user_names_immutable_badge',
                                                                    defaultMessage: 'Managed',
                                                                })}
                                                            />
                                                        </Box>
                                                    </C1Tooltip>
                                                )}
                                            </Stack>
                                        </Box>
                                    </Grid>
                                </Grid>
                            )
                        }
                        return <></>
                    })}
            </>
        )
    }

    return (
        <>
            {expandable ? (
                <Box sx={UserStyles.expandableUserListContainer}>
                    <Collapse in={expanded} collapsedSize="24px" sx={UserStyles.userList}>
                        <UserList />
                    </Collapse>
                    {proposedOptions.length > 1 && !expanded && (
                        <IconButton
                            size="small"
                            color="primary"
                            aria-label="Show more assignees"
                            onClick={(): void => {
                                setExpanded(true)
                            }}
                        >
                            <Typography variant="h6" component="span">
                                +
                                {' '}
                                {proposedOptions.length - 1}
                            </Typography>
                        </IconButton>
                    )}
                </Box>
            ) : (
                <UserList />
            )}
            {emptyState}
        </>
    )
}

export const renderOption = (props: React.HTMLAttributes<HTMLLIElement>, option: User): ReactNode => (
    <li {...props} key={option.id} data-dd-action-name="Select user">
        <UserDisplayName user={option} readOnly />
    </li>
)

const dialogContentStyles = {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    height: 'calc(100vh - 250px)',
    overflow: 'hidden',
}

const getDisplayFilters = (initialRequest: SearchUsersRequest): UserFilterKeys[] => {
    const displayFilters: UserFilterKeys[] = []

    if (initialRequest.userStatuses.length === 0) {
        displayFilters.push('userStatuses')
    }
    if (initialRequest.roleIds.length === 0) {
        displayFilters.push('roleIds')
    }
    if (initialRequest.departments.length === 0) {
        displayFilters.push('departments')
    }
    if (initialRequest.jobTitles.length === 0) {
        displayFilters.push('jobTitles')
    }
    if (initialRequest.managerIds.length === 0) {
        displayFilters.push('managerIds')
    }

    displayFilters.push('query')

    return displayFilters
}

const UsersSelectionsModal: FC<SelectionsModalProps<UserValue, SearchUsersRequest>> = ({
    currentSelections,
    onSelectionChange,
    initialRequest,
    close,
}) => {
    const [selectedUsers, setSelectedUsers] = useState<UserValue[]>(currentSelections)

    const handleSave = (): void => {
        onSelectionChange(selectedUsers)
        close()
    }

    const handleCancel = (): void => {
        close()
    }

    return (
        <C1Dialog maxWidth="lg" fullWidth open onClose={handleCancel}>
            <C1DialogTitle>
                <FormattedMessage
                    id="component_enhanced_user_search_modal_title"
                    defaultMessage="Select users"
                />
            </C1DialogTitle>
            <C1DialogContent>
                <Stack sx={dialogContentStyles}>
                    <C1UsersList
                        columns={[
                            UserListColumns.user,
                            UserListColumns.status,
                            UserListColumns.email,
                            UserListColumns.jobTitle,
                            UserListColumns.department,
                        ]}
                        setSelections={setSelectedUsers}
                        selections={selectedUsers}
                        filter={{ ...initialRequest, excludeIds: initialRequest.excludeIds }}
                        displayFilters={getDisplayFilters(initialRequest)}
                    />
                </Stack>
            </C1DialogContent>
            <C1DialogActions>
                <C1Button onClick={handleCancel} variant="outlined">
                    <FormattedMessage
                        id="component_enhanced_user_search_modal_cancel"
                        defaultMessage="Cancel"
                    />
                </C1Button>
                <C1Button onClick={handleSave} variant="contained" color="primary">
                    <FormattedMessage
                        id="component_enhanced_user_search_modal_save"
                        defaultMessage="Apply selection"
                    />
                </C1Button>
            </C1DialogActions>
        </C1Dialog>
    )
}

const SingleUserSelectionsModal: FC<SingleSelectionModalProps<UserValue, SearchUsersRequest>> = ({
    onSelectionChange,
    initialRequest,
    close,
}) => {
    const handleRowClick = (_: React.MouseEvent | React.KeyboardEvent, user: UserValue): void => {
        onSelectionChange(user)
        close()
    }

    const handleCancel = (): void => {
        close()
    }

    return (
        <C1Dialog maxWidth="lg" fullWidth open onClose={handleCancel}>
            <C1DialogTitle>
                <FormattedMessage
                    id="component_single_user_search_modal_title"
                    defaultMessage="Select user"
                />
            </C1DialogTitle>
            <C1DialogContent>
                <Stack sx={dialogContentStyles}>
                    <C1UsersList
                        columns={[
                            UserListColumns.userReadOnly,
                            UserListColumns.status,
                            UserListColumns.email,
                            UserListColumns.jobTitle,
                            UserListColumns.department,
                        ]}
                        onRowClick={handleRowClick}
                        filter={{ ...initialRequest, excludeIds: initialRequest.excludeIds }}
                        displayFilters={getDisplayFilters(initialRequest)}
                    />
                </Stack>
            </C1DialogContent>
            <C1DialogActions>
                <C1Button onClick={handleCancel} variant="outlined">
                    <FormattedMessage
                        id="component_single_user_search_modal_cancel"
                        defaultMessage="Cancel"
                    />
                </C1Button>
            </C1DialogActions>
        </C1Dialog>
    )
}

export type SearchUsersProps = Omit<
    ComponentProps<typeof C1AutocompleteMultipleAsyncHook<SearchUsersRequest, UserValue>>,
    'Req' | 'hook' | 'renderOption' | 'renderLabel' | 'initialValue'
> & {
    baseRequest?: Partial<SearchUsersRequest>
    users?: User[]
    onSelected: (users: User[]) => void
    disabled?: boolean
    showEnhancedSearch?: boolean
    /** User IDs that cannot be removed (e.g., connector-sourced owners) */
    lockedUserIds?: string[]
    /** Tooltip text shown on locked user chips */
    lockedUserTooltip?: string
}

export const SearchUsers: React.FC<SearchUsersProps> = ({
    users,
    baseRequest,
    showEnhancedSearch = true,
    label: _label,
    lockedUserIds,
    lockedUserTooltip,
    ...rest }) => {
    const intl = useIntl()
    const req = {
        create: (q: Partial<SearchUsersRequest>): SearchUsersRequest => SearchUsersRequest.create({ ...q, ...baseRequest }),
    }

    const defaultLockedTooltip = intl.formatMessage({
        id: 'search_users_immutable_tooltip',
        defaultMessage: 'This owner is managed by the system and cannot be removed',
    })

    const renderTags = useMemo(
        () => (lockedUserIds?.length
            ? renderTagsWithLocked<UserValue>(lockedUserIds, lockedUserTooltip || defaultLockedTooltip)
            : undefined),
        [lockedUserIds, lockedUserTooltip, defaultLockedTooltip],
    )

    return <C1AutocompleteMultipleAsyncHook<SearchUsersRequest, UserValue>
        {...rest}
        hook={userSearchApi.useLazySearch}
        Req={req}
        renderOption={renderOption}
        renderLabel={renderLabel}
        initialValue={users}
        SelectionsModal={showEnhancedSearch ? UsersSelectionsModal : undefined}
        label={_label === false
            ? undefined
            : _label || intl.formatMessage({ id: 'SelectOneUserHook_label', defaultMessage: 'User' })}
        renderTags={renderTags}
        lockedValues={lockedUserIds}
    />
}

export type SelectOneAppUserComponentsProps = RenderedSelectOneProps<AppUserViewExpanded>
export const SearchAppUsersSingleSelect: React.FC<SelectOneAppUserComponentsProps> = (
    rest: SelectOneAppUserComponentsProps,
) => {
    return <SelectOne<AppUserViewExpanded>
        renderOption={
            (
                props: React.HTMLAttributes<HTMLLIElement>,
                option: AppUserViewExpanded,
            ): ReactNode => (
                <li {...props} key={option.id} data-dd-action-name="Select app user">
                    <AppUserDisplayName
                        appUser={option.appUser}
                        app={option.app}
                        readOnly
                    />
                </li>
            )
        }
        renderLabel={
            (option: AppUserViewExpanded): string =>
                option.appUser?.displayName || option.id
        }
        {...rest}
    />
}

export type SelectOneUserSimpleComponentProps = RenderedSelectOneProps<UserValue>
export const SelectOneUserSimpleComponent = (props: SelectOneUserSimpleComponentProps): React.ReactElement => (
    <SelectOne<UserValue> {...props} renderOption={renderOption} renderLabel={renderLabel} />
)

const renderAppUserOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: AppUserValue,
): ReactNode => (
    <li {...props} key={option.id} data-dd-action-name="Select app user">
        <AppUserDisplayName
            appUser={option}
            app={option.app}
            readOnly
        />
    </li>
)

const renderUserOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: UserValue,
): ReactNode => (
    <li {...props} key={option.id} data-dd-action-name="Select user">
        <UserDisplayName
            user={option}
            readOnly
        />
    </li>
)

const expandAll = { paths: ['*'] }

const getAppUserDisplayFilters = (initialRequest: AppUserServiceSearchRequest): AppUserFilterKeys[] => {
    const displayFilters: AppUserFilterKeys[] = []

    if (initialRequest.appUserStatuses.length === 0) {
        displayFilters.push('appUserStatuses')
    }
    if (initialRequest.appUserTypes.length === 0) {
        displayFilters.push('appUserTypes')
    }
    if (initialRequest.appUserDomains.length === 0) {
        displayFilters.push('appUserDomains')
    }

    displayFilters.push('query')

    return displayFilters
}

const SingleAppUserSelectionsModal: FC<SingleSelectionModalProps<AppUserValue, AppUserServiceSearchRequest>> = ({
    onSelectionChange,
    initialRequest,
    close,
}) => {
    const handleRowClick = useCallback(
        (_event: React.MouseEvent | React.KeyboardEvent, appUser: AppUserValue): void => {
            onSelectionChange(appUser)
            close()
        },
        [onSelectionChange, close],
    )

    const handleCancel = useCallback((): void => {
        close()
    }, [close])

    return (
        <C1Dialog open onClose={handleCancel} maxWidth="lg" fullWidth>
            <C1DialogTitle>
                <FormattedMessage
                    id="component_single_app_user_search_modal_title"
                    defaultMessage="Select account"
                />
            </C1DialogTitle>
            <C1DialogContent>
                <Stack spacing={2} sx={dialogContentStyles}>
                    <C1AppUsersList
                        columns={[AppUserListColumns.account, AppUserListColumns.app, AppUserListColumns.status]}
                        onRowClick={handleRowClick}
                        filter={{ ...initialRequest }}
                        displayFilters={getAppUserDisplayFilters(initialRequest)}
                    />
                </Stack>
            </C1DialogContent>
            <C1DialogActions>
                <C1Button onClick={handleCancel} variant="outlined">
                    <FormattedMessage
                        id="component_single_app_user_search_modal_cancel"
                        defaultMessage="Cancel"
                    />
                </C1Button>
            </C1DialogActions>
        </C1Dialog>
    )
}

type SelectOneAppUserHookProps = Omit<SelectOneHookProps<AppUserServiceSearchRequest, AppUserValue>, 'initialRef'> & {
    appUser?: AppUserValue
    initAppUserRef?: {
        id: string
        appId: string
    }
    onSelected: (au: AppUserValue) => void
    hideLabel?: boolean
    showEnhancedSearch?: boolean
}

export const SelectOneAppUserHook: FC<SelectOneAppUserHookProps> = ({
    appUser: initialValue,
    initAppUserRef,
    hideLabel = false,
    showEnhancedSearch = true,
    ...rest }) => {
    const intl = useIntl()
    return <C1AutocompleteSingleAsyncHook<AppUserServiceSearchRequest, AppUserValue>
        placeholder={intl.formatMessage({ id: 'SelectOneAppUserHook_placeholder', defaultMessage: 'Select an account' })}
        label={hideLabel ? undefined : intl.formatMessage({ id: 'SelectOneAppUserHook_label', defaultMessage: 'Account' })}
        {...rest}
        type={AppUser}
        hook={searchAppUsersApi.useLazySearch}
        Req={AppUserServiceSearchRequest}
        reqExpandMask={expandAll}
        renderOption={renderAppUserOption}
        renderLabel={renderLabel}
        initialValue={initialValue}
        initialRef={initAppUserRef ? AppUserRef.create({ appId: initAppUserRef.appId, id: initAppUserRef.id }) : undefined}
        SingleSelectionModal={showEnhancedSearch ? SingleAppUserSelectionsModal : undefined}
    />
}

type SelectOneUserHookProps = SelectOneHookProps<SearchUsersRequest, UserValue> & {
    user?: UserValue
    initUserId?: string
    onSelected: (u: UserValue) => void
    hideLabel?: boolean
    baseRequest?: Partial<SearchUsersRequest>
    sx?: SxProps
    showEnhancedSearch?: boolean
}

export const SelectOneUserHook: FC<SelectOneUserHookProps> = ({
    user: initialValue,
    initUserId,
    hideLabel = false,
    baseRequest,
    showEnhancedSearch = true,
    ...rest
}) => {
    const intl = useIntl()

    const req = {
        create: (q: Partial<SearchUsersRequest>): SearchUsersRequest => SearchUsersRequest.create({ ...q, ...baseRequest }),
    }

    return <C1AutocompleteSingleAsyncHook<SearchUsersRequest, UserValue>
        initialRef={initUserId ? UserRef.create({ id: initUserId }) : undefined}
        label={hideLabel ? undefined : intl.formatMessage({ id: 'SelectOneUserHook_label', defaultMessage: 'User' })}
        {...rest}
        placeholder={intl.formatMessage({ id: 'SelectOneUserHook_placeholder', defaultMessage: 'Select a user' })}
        type={User}
        hook={userSearchApi.useLazySearch}
        Req={req}
        reqExpandMask={expandAll}
        renderOption={renderUserOption}
        renderLabel={renderLabel}
        initialValue={initialValue}
        SingleSelectionModal={showEnhancedSearch ? SingleUserSelectionsModal : undefined}
    />
}

const RenderAgentOption = (props: React.HTMLAttributes<HTMLLIElement>, option: AgentView): ReactNode => {
    const intl = useIntl()
    let instructions: ReactNode | null = null

    if (option.agentInstructionsType === AgentInstructionsType.CUSTOM) {
        instructions = <FormattedMessage
            id="agents_table_instructions_custom"
            defaultMessage="Custom: {instructions}"
            values={{ instructions: option.instructions }}
        />
    } else if (option.agentInstructionsType !== AgentInstructionsType.UNSPECIFIED) {
        instructions = AgentInstructionsTypeToText(intl, option.agentInstructionsType)
    }

    return <li {...props} key={option.id}>
        <Stack spacing={1}>
            <Typography>{option.user?.displayName || option.id}</Typography>
            {instructions && <Typography sx={{ color: 'text.secondary' }}>{instructions}</Typography>}
        </Stack>
    </li>
}

const renderAgentLabel = (option: AgentView): string => option.user?.displayName || option.id

type SelectOneAgentUserHookProps = SelectOneHookProps<SearchAgentsRequest, AgentView> & {
    baseRequest?: Partial<SearchAgentsRequest>
    agent?: AgentView
    initAgentRef?: {
        id: string
    }
    onSelected: (agent: AgentView) => void
    hideLabel?: boolean
}

export const SelectOneAgentUserHook: FC<SelectOneAgentUserHookProps> = ({
    agent: initialValue,
    initAgentRef,
    hideLabel = false,
    baseRequest,
    ...rest
}) => {
    const intl = useIntl()

    const req = {
        create: (q: Partial<SearchAgentsRequest>): SearchAgentsRequest => SearchAgentsRequest.create({ ...q, ...baseRequest }),
    }

    return <C1AutocompleteSingleAsyncHook<SearchAgentsRequest, AgentView>
        initialRef={initAgentRef ? AgentRef.create({ id: initAgentRef.id }) : undefined}
        label={hideLabel ? undefined : intl.formatMessage({ id: 'SelectOneAgentUserHook_label', defaultMessage: 'Agent' })}
        {...rest}
        placeholder={intl.formatMessage({ id: 'SelectOneAgentUserHook_placeholder', defaultMessage: 'Select an agent' })}
        type={AgentView}
        hook={searchAgentsApi.useLazySearch}
        Req={req}
        renderOption={RenderAgentOption}
        renderLabel={renderAgentLabel}
        initialValue={initialValue}
    />
}

type SearchManagedUsersARPropsSingle = SelectOneHookProps<SearchManagedUsersForAccessRequestsRequest, UserValue> & {
    user?: User
    managerId: string
    onSelected: (user: User) => void
    baseRequest?: Partial<SearchManagedUsersForAccessRequestsRequest>
}

export const SearchManagedUsersARSingleSelect: React.FC<SearchManagedUsersARPropsSingle> = ({
    user,
    managerId,
    baseRequest,
    ...rest
}) => {
    const req = {
        create: (q: Partial<SearchManagedUsersForAccessRequestsRequest>) =>
            SearchManagedUsersForAccessRequestsRequest.create({
                query: q?.query,
                managerId,
                ...baseRequest,
            }),
    }
    return (
        <C1AutocompleteSingleAsyncHook<SearchManagedUsersForAccessRequestsRequest, UserValue>
            {...rest}
            hook={managedUserSearchForAccessRequestsApi.useLazySearchManagedUsersForAccessRequests}
            type={User}
            Req={req}
            renderOption={renderOption}
            renderLabel={renderLabel}
            initialValue={user}
        />
    )
}

type SearchUsersFromIdsProps = {
    ids: (string | undefined)[] | undefined
    onSelected: (ids: string[]) => void
} & Omit<SearchUsersProps, 'users'>

export const SearchUsersFromIds: React.FC<SearchUsersFromIdsProps> = ({ ids, onSelected: _onSelected, ...rest }) => {
    const refs = ids ? ids.map((id) => UserRef.create({ id })) : []
    const hasUserOrigin = useFeatureChecker(FeatureFlagIdUserOrigin)

    const { data, trigger, error } = userSearchApi.useLazySearch()

    useEffect(() => {
        if (refs.length > 0) trigger(SearchUsersRequest.create({ refs }))
    }, [trigger]) // eslint-disable-line react-hooks/exhaustive-deps

    const onSelected = (users: UserValue[]): void => {
        _onSelected(users.map((user) => user.id))
    }

    if (error) return <APIError err={error} />

    const baseRequest = hasUserOrigin
        ? { excludeOrigins: [UserOrigin.LOCAL, UserOrigin.SYSTEM] }
        : { excludeTypes: [UserType.AGENT] }

    return <SearchUsers
        users={data[0] as UserValue[]}
        baseRequest={baseRequest}
        onSelected={onSelected}
        {...rest}
    />
}
